import { ChipLabelSize, ChipLabelVariant } from 'omakase/ChipLabel/ChipLabel';
import { Theme, css } from '@emotion/react';
import { assertNever } from '@tock/shared/utils/typeUtils';

const getPaddingFromSize = ({ spacing }: Theme, size: ChipLabelSize) => {
  switch (size) {
    case 'default': {
      return `
        2px ${spacing(1.5)}px;
        min-height: ${spacing(2.5)}px;
      `;
    }
    case 'medium': {
      return `
        4px ${spacing(2)}px;
        min-height: ${spacing(3)}px;
      `;
    }
    case 'large': {
      return `
        4px ${spacing(2.5)}px;
        min-height: ${spacing(4)}px;
      `;
    }
    default:
      assertNever(size);
  }
};

const getTypographyFromSize = ({ typography }: Theme, size: ChipLabelSize) => {
  switch (size) {
    case 'default': {
      return typography.variant('captionStrong');
    }
    case 'medium': {
      return typography.variant('captionStrong');
    }
    case 'large': {
      return typography.variant('body02Strong');
    }
    default:
      assertNever(size);
  }
};

interface ChipLabelOptions {
  highContrast?: boolean;
}

const getTextColorFromVariant = (
  { colors }: Theme,
  variant: ChipLabelVariant,
  { highContrast }: ChipLabelOptions
) => {
  return highContrast && (variant === 'basic' || variant === 'ghost')
    ? colors.fgContrast
    : colors.fgDefault;
};

const getBackgroundColorFromVariant = (
  { colors }: Theme,
  variant: ChipLabelVariant,
  { highContrast }: ChipLabelOptions
) => {
  switch (variant) {
    case 'basic':
      return highContrast ? colors.bgContrast : colors.bgDefault;
    case 'ghost':
      return 'transparent';
    case 'success':
      return colors.bgSuccessLighter;
    case 'warning':
      return colors.bgWarningLighter;
    case 'danger':
      return colors.bgDangerLighter;
    case 'information':
      return colors.bgPrimaryLighter;
    case 'subtle':
      return colors.bgLighter;
    default:
      assertNever(variant);
  }
};

const getBoxShadowFromVariant = (
  { colors }: Theme,
  variant: ChipLabelVariant,
  { highContrast }: ChipLabelOptions
) => {
  if (!highContrast) {
    return 'none';
  }

  const boxShadowBase = 'inset 0 0 0 1px ';

  switch (variant) {
    case 'basic':
    case 'ghost':
      return 'none';
    case 'success':
      return `${boxShadowBase}${colors.borderSuccessLight}`;
    case 'warning':
      return `${boxShadowBase}${colors.borderWarningLight}`;
    case 'danger':
      return `${boxShadowBase}${colors.borderDangerLight}`;
    case 'information':
      return `${boxShadowBase}${colors.borderPrimaryLight}`;
    case 'subtle':
      return `${boxShadowBase}${colors.borderDefault}`;
    default:
      assertNever(variant);
  }
};

export const chipLabelBase = (
  theme: Theme,
  size: ChipLabelSize,
  variant: ChipLabelVariant,
  options: ChipLabelOptions
) => css`
  ${getTypographyFromSize(theme, size)}

  background-color: ${getBackgroundColorFromVariant(theme, variant, options)};
  box-shadow: ${getBoxShadowFromVariant(theme, variant, options)};
  color: ${getTextColorFromVariant(theme, variant, options)};
  display: inline-flex;
  align-items: center;
  padding: ${getPaddingFromSize(theme, size)};
`;
